import React, { useState } from 'react'
import { Button, Col, Row, Spin, Empty, App } from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { AppCard, PageLoading } from '@/shared/components/ui'
import { useI18n } from '@/shared/i18n'
import { useAuthStore } from '@/stores/authStore'
import {
  useConnectorProviders,
  useConnectorConfigs,
  useConnectorStatus,
  useDeleteConnectorConfig,
  useTestConnector,
} from '@/shared/api/connectorApi'
import ConnectorCard from './components/ConnectorCard'
import ConnectorConfigDrawer from './components/ConnectorConfigDrawer'
import type { ConnectorConfig } from './types'

const ConnectorManagement: React.FC = () => {
  const { t } = useI18n('modules')
  const { company } = useAuthStore()
  const companyId = company?.id
  const { message } = App.useApp()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState<ConnectorConfig | null>(null)

  const { data: providers = [], isLoading: loadingProviders } = useConnectorProviders()
  const { data: configs = [], isLoading: loadingConfigs, refetch: refetchConfigs } = useConnectorConfigs(companyId)
  const { data: statuses = {}, refetch: refetchStatus } = useConnectorStatus(companyId)
  const deleteMutation = useDeleteConnectorConfig()
  const testMutation = useTestConnector()

  const handleAdd = () => {
    setEditingConfig(null)
    setDrawerOpen(true)
  }

  const handleEdit = (config: ConnectorConfig) => {
    setEditingConfig(config)
    setDrawerOpen(true)
  }

  const handleDelete = async (provider: string) => {
    if (!companyId) return
    try {
      await deleteMutation.mutateAsync({ companyId, provider })
      message.success(t('connectors.deleted'))
    } catch (e: unknown) {
      const errorMsg = e instanceof Error ? e.message : t('connectors.deleteFailed')
      message.error(errorMsg)
    }
  }

  const handleTest = async (config: ConnectorConfig) => {
    if (!companyId) return
    try {
      const result = await testMutation.mutateAsync({ companyId, config })
      if (result.healthy) {
        message.success(`${config.provider} ${t('connectors.connectionHealthy')} (${result.latency_ms}ms)`)
      } else {
        message.error(result.error || `${config.provider} ${t('connectors.connectionFailed')}`)
      }
      refetchStatus()
    } catch (e: unknown) {
      const errorMsg = e instanceof Error ? e.message : t('connectors.testFailed')
      message.error(errorMsg)
    }
  }

  const handleRefresh = () => {
    refetchConfigs()
    refetchStatus()
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
    setEditingConfig(null)
  }

  const isLoading = loadingProviders || loadingConfigs

  return (
    <div style={{ padding: '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>{t('connectors.title')}</h1>
        <div>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} style={{ marginRight: 8 }}>
            {t('connectors.refresh')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {t('connectors.addConnector')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <PageLoading />
      ) : configs.length === 0 ? (
        <AppCard>
          <Empty description={t('connectors.emptyDescription')}>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              {t('connectors.addFirst')}
            </Button>
          </Empty>
        </AppCard>
      ) : (
        <Row gutter={[16, 16]}>
          {configs.map(config => (
            <Col key={config.provider} xs={24} md={12} xl={8}>
              <ConnectorCard
                config={config}
                status={statuses[config.provider]}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onTest={handleTest}
              />
            </Col>
          ))}
        </Row>
      )}

      {companyId && (
        <ConnectorConfigDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          providers={providers}
          companyId={companyId}
          editingConfig={editingConfig}
        />
      )}
    </div>
  )
}

export default ConnectorManagement
